import FieldRow from 'components/FormElements/FieldRow';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import { SelectChangeEvent, styled, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import Spacing from 'components/Spacing';
import {
  CORE_FEE_TEMPLATE_ADD,
  PERFORMANCE_FEE_TEMPLATE_ADD,
} from 'adminConstants/routes';
import Button from 'components/Button';
import api from 'lib/trpcClient';
import Table from 'components/Table';
import {
  FeeChargingFrequency,
  FeeHurdleBasis,
  FeeType,
  FeeCalculationBasis,
} from 'further-types/investment';
import { FeeAccounting } from 'further-types/investment';
import lookupDisplayFeeType from 'utils/lookup-display-fee-type';
import { numberToCurrencyString } from 'further-ui/utils';
import {
  CoreFeeTypeLabels,
  FeeCalculationBasisLabels,
  FeeChargingFrequencyLabels,
} from 'further-ui/labels';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const FormContainer = styled('div')`
  max-width: 1020px;
  padding-bottom: 10px;
`;

type CoreFee = {
  feeName: string;
  feeType: FeeType;
  feeCalculationBasis: FeeCalculationBasis;
  netFee: number;
  grossFee: number;
  vat: number;
  chargingFrequency?: FeeChargingFrequency | null;
  startingPeriod?: number | null;
  finishingPeriod?: number | null;
  feeAccounting: FeeAccounting;
};

type PerformanceFee = {
  includeFeesInHurdle: boolean;
  hurdleBasis: FeeHurdleBasis;
  feeName: string;
  feeType: FeeType;
  feePercentageIncVAT: number;
  feePercentageExVAT: number;
  vatPercentage: number;
};

const CreateTemplateButton = styled(Button)({
  padding: 0,
  textDecoration: 'underline',
  fontSize: '14px',
});

type FeeManagement = {
  key: string;
  defaultCoreFee: string;
  defaultPerformanceFee: string;
};

type Props = {
  feeManagement: FeeManagement;
  setFeeManagement: (feeManagement: FeeManagement) => void;
  coreFeeTemplates: Array<{
    _id: string;
    name: string;
  }>;
  performanceFeeTemplates: Array<{
    _id: string;
    name: string;
  }>;
  refetchCoreFeeTemplates: () => void;
  refetchPerformanceFeeTemplates: () => void;
  disabled: boolean;
};

const FeeManagement: React.FC<Props> = ({
  feeManagement,
  setFeeManagement,
  coreFeeTemplates,
  performanceFeeTemplates,
  refetchCoreFeeTemplates,
  refetchPerformanceFeeTemplates,
  disabled = false,
}) => {
  const [canCreateCoreFeeTemplate, canCreatePerformanceFeeTemplate] =
    useHasPermissions([
      RolePermission.CreateCoreFees,
      RolePermission.CreatePerformanceFees,
    ]);

  const { data: coreFeeTemplate, isLoading: isLoadingCoreFeeTemplate } =
    api.coreFeeTemplates.getById.useQuery(
      { id: feeManagement.defaultCoreFee },
      { enabled: !!feeManagement.defaultCoreFee },
    );

  const {
    data: performanceFeeTemplate,
    isLoading: isLoadingPerformanceFeeTemplate,
  } = api.performanceFeeTemplates.getById.useQuery(
    { id: feeManagement.defaultPerformanceFee },
    { enabled: !!feeManagement.defaultPerformanceFee },
  );

  const coreFeeColumns = [
    {
      label: 'Fee name',
      key: 'feeName',
    },
    {
      label: 'Fee type',
      key: 'feeType',
      render: (elm: CoreFee) => {
        return CoreFeeTypeLabels[
          lookupDisplayFeeType(elm.feeType, elm.feeCalculationBasis)
        ];
      },
    },
    {
      label: 'Charging frequency',
      key: 'chargingFrequency',
      render: (elm: CoreFee) => {
        return FeeChargingFrequencyLabels[elm.chargingFrequency || ''];
      },
    },
    {
      label: 'Fee basis',
      key: 'feeBasis',
      render: (elm: CoreFee) => {
        return FeeCalculationBasisLabels[elm.feeCalculationBasis || ''];
      },
    },
    {
      label: 'Start period',
      key: 'startingPeriod',
    },
    {
      label: 'Finish period',
      key: 'finishingPeriod',
    },
    {
      label: 'Fee %/£ (ex VAT)',
      key: 'netFee',
      render: (elm: CoreFee) => {
        return elm.feeCalculationBasis ===
          FeeCalculationBasis.PercentageOfTotalCommitment
          ? `${elm.netFee}%`
          : `£${elm.netFee}`;
      },
    },
    {
      label: 'VAT',
      key: 'vat',
      render: (elm: CoreFee) => {
        return elm.vat === 0 ? 'No VAT' : `${elm.vat}%`;
      },
    },
    {
      label: 'Gross fee',
      key: 'grossFee',
      render: (elm: CoreFee) => {
        return elm?.feeCalculationBasis === FeeCalculationBasis.Fixed
          ? numberToCurrencyString(elm?.grossFee)
          : Intl.NumberFormat('en-GB', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 10,
            }).format(elm.grossFee) + '%';
      },
    },
    {
      label: 'Fee accounting',
      key: 'feeAccounting',
      render: (elm: CoreFee) => {
        return {
          [FeeAccounting.ChargedToInvestor]: 'Charged to investor',
          [FeeAccounting.Accrued]: 'Accrued',
        }[elm.feeAccounting];
      },
    },
  ];

  const performanceFeeColumns = [
    {
      label: 'Fee name',
      key: 'feeName',
    },
    {
      label: 'Hurdle basis',
      key: 'hurdleBasis',
      render: (elm: PerformanceFee) => {
        return elm.hurdleBasis === FeeHurdleBasis.Company
          ? 'Company'
          : 'Subscription';
      },
    },
    {
      label: 'Fee treatment',
      key: 'includeFeesInHurdle',
      render: (elm: PerformanceFee) => {
        return elm.includeFeesInHurdle
          ? 'Subscription including fees'
          : 'Subscription minus fees';
      },
    },
    {
      label: 'Hurdle (1.0 = 1.0x return)',
      key: 'hurdle',
    },

    {
      label: 'Fee (ex VAT)',
      key: 'feePercentageExVAT',
      render: (elm: PerformanceFee) => {
        return `${Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(elm.feePercentageExVAT)}%`;
      },
    },
    {
      label: 'VAT',
      key: 'vatPercentage',
      render: (elm: PerformanceFee) => {
        return elm.vatPercentage === 0 ? 'No VAT' : `${elm.vatPercentage}%`;
      },
    },
    {
      label: 'Gross fee',
      key: 'feePercentageIncVAT',
      render: (elm: PerformanceFee) => {
        return `${Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(elm.feePercentageIncVAT)}%`;
      },
    },
  ];

  return (
    <Spacing size="lg">
      <Spacing size="sm">
        <Typography variant="h6">Choose default core fee</Typography>
        <Typography variant="body2" paddingBottom={2} maxWidth={1050}>
          Please select a template for the default initial and recurring fees.
          If individual investors have different core fee settings, this can be
          set on that investor's <i>Edit Subscription</i> page.{' '}
          {canCreateCoreFeeTemplate
            ? "Haven't created a core fee template yet? "
            : ''}
          {canCreateCoreFeeTemplate ? (
            <Link
              to={CORE_FEE_TEMPLATE_ADD}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CreateTemplateButton variant="text" disabled>
                Click here to create one
              </CreateTemplateButton>
              .
            </Link>
          ) : null}
        </Typography>

        <FormContainer>
          <FieldRow title="Choose core fee template" centerTitle narrow>
            <AppSelectBox
              maxWidth={500}
              disabled={disabled}
              value={feeManagement.defaultCoreFee}
              onOpen={() => {
                refetchCoreFeeTemplates();
                refetchPerformanceFeeTemplates();
              }}
              onChange={(event: SelectChangeEvent<unknown>) => {
                setFeeManagement({
                  ...feeManagement,
                  defaultCoreFee: event.target.value as string,
                });
              }}
              data={[
                {
                  title: 'No fees',
                  value: '',
                },
                ...coreFeeTemplates.map((template) => ({
                  title: template.name,
                  value: template._id,
                })),
              ]}
              valueKey="value"
            />
          </FieldRow>
        </FormContainer>

        {coreFeeTemplate?.coreFees && !!feeManagement.defaultCoreFee && (
          <Table
            columns={coreFeeColumns}
            tablebody={coreFeeTemplate.coreFees}
            variant="nohover"
            loading={isLoadingCoreFeeTemplate}
            hideFooterUnderline
          />
        )}
      </Spacing>

      <Spacing size="sm">
        <Typography variant="h6">Choose default performance fee</Typography>
        <Typography variant="body2" paddingBottom={2} maxWidth={1050}>
          Please select a template for the default performance fees. If
          individual investors have different performance fee settings, this can
          be set on that investor's <i>Edit Subscription</i> page.{' '}
          {canCreatePerformanceFeeTemplate
            ? "Haven't created a performance fee template yet? "
            : ''}
          {canCreatePerformanceFeeTemplate ? (
            <Link
              to={PERFORMANCE_FEE_TEMPLATE_ADD}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CreateTemplateButton variant="text" disabled>
                Click here to create one
              </CreateTemplateButton>
              .
            </Link>
          ) : null}
        </Typography>

        <FormContainer>
          <FieldRow title="Choose performance fee template" centerTitle narrow>
            <AppSelectBox
              maxWidth={500}
              disabled={disabled}
              value={feeManagement.defaultPerformanceFee}
              onOpen={() => {
                refetchCoreFeeTemplates();
                refetchPerformanceFeeTemplates();
              }}
              onChange={(e: SelectChangeEvent<unknown>) =>
                setFeeManagement({
                  ...feeManagement,
                  defaultPerformanceFee: e.target.value as string,
                })
              }
              data={[
                {
                  title: 'No fees',
                  value: '',
                },
                ...performanceFeeTemplates.map((template) => ({
                  title: template.name,
                  value: template._id,
                })),
              ]}
              valueKey="value"
            />
          </FieldRow>
        </FormContainer>

        {performanceFeeTemplate?.performanceFees &&
          !!feeManagement.defaultPerformanceFee && (
            <Table
              columns={performanceFeeColumns}
              tablebody={performanceFeeTemplate.performanceFees}
              variant="nohover"
              loading={isLoadingPerformanceFeeTemplate}
              hideFooterUnderline
            />
          )}
      </Spacing>
    </Spacing>
  );
};

export default FeeManagement;
